import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/** UX 设计书 §4 键盘表："G 然后 D/I/L/M/A/P" 跳转仪表盘/库存/布局/流水/预警/产品 */
const G_NAV_MAP: Record<string, string> = {
  d: '/dashboard',
  i: '/inventory',
  l: '/layout',
  m: '/movements',
  a: '/alerts',
  p: '/products',
};

const G_CHORD_TIMEOUT_MS = 1200;

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * 全局键盘快捷键（UX 设计书 §4）。挂在 AppShell，全应用生效一次，不是
 * 每个页面各自实现。
 *
 * 范围说明（M11 UX 打磨，刻意的取舍）：`⌘/Ctrl+K`、`G` 连字导航、
 * `⌘/Ctrl+Enter` 提交表单、裸键 `/` 均已实现；表格里没有实现的是"N 新建
 * 当前页主操作"——那需要每个列表页显式声明"我的主操作是什么"，属于给每个
 * 页面单独接线的工作量，本轮没有做，留给后续按页面补齐。
 */
export function useGlobalShortcuts(openPalette: () => void): void {
  const navigate = useNavigate();
  const gPendingRef = useRef(false);
  const gTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      const meta = e.metaKey || e.ctrlKey;

      // ⌘/Ctrl+Enter：提交离当前焦点最近的表单——通用实现，不需要逐个表单接线
      if (meta && e.key === 'Enter') {
        const form = (document.activeElement as HTMLElement | null)?.closest('form');
        if (form) {
          e.preventDefault();
          form.requestSubmit();
        }
        return;
      }

      // ⌘/Ctrl+K：命令面板，即使焦点在输入框里也要生效——这是命令面板"随处可达"的核心承诺
      if (meta && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openPalette();
        return;
      }

      // 以下都是裸键（无修饰键）快捷键：输入框/可编辑区域里必须放行，否则打字会被劫持
      if (isTypingTarget(e.target)) return;

      if (e.key === '/') {
        e.preventDefault();
        const marked = document.querySelector<HTMLElement>('[data-shortcut-focus]');
        if (marked) marked.focus();
        else openPalette();
        return;
      }

      if (gPendingRef.current) {
        gPendingRef.current = false;
        if (gTimeoutRef.current) clearTimeout(gTimeoutRef.current);
        const target = G_NAV_MAP[e.key.toLowerCase()];
        if (target) {
          e.preventDefault();
          navigate(target);
        }
        return;
      }

      if (e.key.toLowerCase() === 'g' && !meta && !e.altKey && !e.shiftKey) {
        gPendingRef.current = true;
        gTimeoutRef.current = setTimeout(() => {
          gPendingRef.current = false;
        }, G_CHORD_TIMEOUT_MS);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (gTimeoutRef.current) clearTimeout(gTimeoutRef.current);
    };
  }, [navigate, openPalette]);
}
